import { useTheme } from "../lib/theme/ThemeProvider";
import FrontierScene from "./FrontierScene";
/**
 * BackgroundLayer — full-bleed brand imagery behind any app surface.
 *
 * Two layered glow systems sit on top of the dark base:
 *
 *   1. Rune breath (ambient).  A screen-blended clone of the image, kept
 *      at low opacity, that breathes between ~12%–38%. Bright pixels in
 *      the image (runes, armor glints, weapon bloom) pulse subtly. Dark
 *      pixels are unaffected because screen-blend(x, 0) = x. The page
 *      stays dark; the figure stays alive.
 *
 *   2. Eye glow (positioned).  Two small radial-gradient lights placed
 *      at the figure's eye positions, screen-blended, holding a higher
 *      floor than the ambient — they're watching you. Coordinates are
 *      per-variant (different images have different focal points).
 *
 * Both respect prefers-reduced-motion.
 *
 * Accessibility: decorative; rendered with aria-hidden and pointer-events-none.
 */

type Variant =
  | "warrior"
  | "warrior+glow"
  | "modes"
  | "doctrine"
  | "lawdogg"
  | "manifesto"
  | "statement"
  | "ctafinal"
  | "berserkr";

type Overlay = "heavy" | "medium" | "light";

interface GlowPoint {
  /** Percentage from left, e.g. "49%". */
  x: string;
  /** Percentage from top, e.g. "38%". */
  y: string;
  /** Bloom diameter in px. Defaults to 80. */
  size?: number;
  /** Core color of the glow. Defaults to ember bright. */
  color?: string;
  /** Animation delay in ms (stagger eyes slightly for organic feel). */
  delayMs?: number;
}

interface VariantConfig {
  layers: string[];
  /** Positioned glow points (eyes, focal runes). Animated wolf-eye pulse. */
  eyes?: GlowPoint[];
}

const VARIANTS: Record<Variant, VariantConfig> = {
  warrior: {
    layers: ["/bg/hero-warrior.jpg"],
    // Bear warrior portrait — eyes guessed at center-upper. Tune in App.tsx via override.
    eyes: [
      { x: "47%", y: "33%", size: 70, delayMs: 0 },
      { x: "53%", y: "33%", size: 70, delayMs: 180 },
    ],
  },
  "warrior+glow": {
    layers: ["/bg/hero-warrior.jpg", "/bg/hero-glow.webp"],
    eyes: [
      { x: "47%", y: "33%", size: 70, delayMs: 0 },
      { x: "53%", y: "33%", size: 70, delayMs: 180 },
    ],
  },
  modes: {
    layers: ["/bg/bg-modes.webp"],
    // Eye glow disabled 2026-05-23 — the warrior's helmet has a dark
    // mask covering the actual eye area, and positioning ember points
    // precisely through the mask slits was a constant battle against
    // the source-image composition. Rune breath alone carries this
    // surface fine. Override at the call site if you want eyes back.
    eyes: [],
  },
  doctrine: {
    layers: ["/bg/bg-doctrine.webp"],
    // Battlefield map / neural net — no eyes per se, but nodes pulse.
  },
  lawdogg: {
    layers: ["/bg/bg-lawdogg.webp"],
    // Wolf in fog — wolf eyes are the whole point.
    eyes: [
      { x: "46%", y: "42%", size: 70, color: "rgba(122,163,201,1)", delayMs: 0 },
      { x: "54%", y: "42%", size: 70, color: "rgba(122,163,201,1)", delayMs: 200 },
    ],
  },
  manifesto: {
    layers: ["/bg/bg-manifesto.webp"],
    eyes: [
      { x: "49%", y: "30%", size: 56, delayMs: 0 },
      { x: "51%", y: "30%", size: 56, delayMs: 160 },
    ],
  },
  statement: {
    layers: ["/bg/bg-statement.webp"],
    // Red-eyed warrior — eyes already red in the source; punch them.
    eyes: [
      { x: "47%", y: "35%", size: 80, delayMs: 0 },
      { x: "53%", y: "35%", size: 80, delayMs: 220 },
    ],
  },
  ctafinal: {
    layers: ["/bg/bg-ctafinal.webp"],
    // Floating holographic documents — no eyes.
  },
  berserkr: {
    layers: ["/bg/bg-berserkr-card.webp"],
    eyes: [
      { x: "47%", y: "34%", size: 70, delayMs: 0 },
      { x: "53%", y: "34%", size: 70, delayMs: 200 },
    ],
  },
};

const OVERLAY_GRADIENT: Record<Overlay, string> = {
  heavy:
    "linear-gradient(180deg, rgba(5,6,8,0.78) 0%, rgba(5,6,8,0.90) 50%, rgba(5,6,8,0.95) 100%)",
  medium:
    "linear-gradient(180deg, rgba(5,6,8,0.62) 0%, rgba(5,6,8,0.78) 50%, rgba(5,6,8,0.90) 100%)",
  light:
    "linear-gradient(180deg, rgba(5,6,8,0.42) 0%, rgba(5,6,8,0.62) 50%, rgba(5,6,8,0.82) 100%)",
};

const VIGNETTE =
  "radial-gradient(ellipse at center, rgba(5,6,8,0) 0%, rgba(5,6,8,0) 35%, rgba(5,6,8,0.55) 100%)";

interface Props {
  variant?: Variant;
  overlay?: Overlay;
  /** Disable the slow Ken Burns scale drift. */
  staticImage?: boolean;
  /** Turn off the ambient rune breath (page becomes flatly dark over the photo). */
  disableRuneBreath?: boolean;
  /** Override positioned eye glow points. Pass [] to disable entirely. */
  eyes?: GlowPoint[];
  /**
   * Flat darkening scrim laid over the whole background (0–1) so foreground
   * panels stay readable. Defaults to 0.8 (80%). Pass 0 to disable.
   */
  scrim?: number;
}

function BackgroundLayer({
  variant = "modes",
  overlay = "heavy",
  staticImage = false,
  disableRuneBreath = false,
  eyes,
  scrim = 0.8,
}: Props) {
  const { themeId } = useTheme();
  const scrimAlpha = Math.min(Math.max(scrim, 0), 1);
  const scrimStyle =
    scrimAlpha > 0 ? { background: `rgba(5,6,8,${scrimAlpha})` } : undefined;

  if (themeId === "frontier") {
    return (
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-black">
        <FrontierScene variant={variant} />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,5,3,0.82) 0%, rgba(8,5,3,0.55) 26%, rgba(8,5,3,0.30) 50%, rgba(8,5,3,0.62) 100%)",
          }}
        />
        {scrimStyle ? <div className="absolute inset-0" style={scrimStyle} /> : null}
      </div>
    );
  }
  const config = VARIANTS[variant];
  const layers = config.layers;
  const baseImg = layers[0]!;

  // Dev tuning: URL params override eye positions live so you can
  // dial them in without code edits.
  //   ?eyeY=7         (sets shared y%)
  //   &eyeX1=47.5     (left eye x%)
  //   &eyeX2=52.5     (right eye x%)
  //   &eyeSize=45     (bloom diameter)
  //   &eyeHide=1      (hide entirely)
  const devTuned = typeof window !== "undefined"
    ? readDevEyeOverrides(window.location.search, config.eyes ?? [])
    : null;

  const eyePoints = eyes ?? devTuned ?? config.eyes ?? [];

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden bg-black"
    >
      {/* BASE LAYER(S) — the photo, dark by default. */}
      {layers.map((path, idx) => (
        <img
          key={`base-${path}`}
          src={path}
          alt=""
          className={`absolute inset-0 h-full w-full select-none object-cover ${
            staticImage ? "" : "motion-safe:animate-kenburns"
          } ${idx > 0 ? "mix-blend-screen opacity-90" : ""}`}
          decoding="async"
          loading="eager"
          fetchPriority={idx === 0 ? "high" : "low"}
          draggable={false}
        />
      ))}

      {/* DARKENING — the page stays dark over the image. */}
      <div
        className="absolute inset-0"
        style={{ background: OVERLAY_GRADIENT[overlay] }}
      />

      {/* RUNE BREATH — bright-pixel pulse. Screen-blend means only the
          highlights in the image lift; dark areas stay dark under the
          overlay. Kept at low opacity floor so it never dominates. */}
      {!disableRuneBreath ? (
        <img
          src={baseImg}
          alt=""
          className={`absolute inset-0 h-full w-full select-none object-cover mix-blend-screen ${
            staticImage ? "" : "motion-safe:animate-kenburns"
          } motion-safe:animate-rune-breath`}
          style={{
            filter: "brightness(1.4) contrast(1.25) saturate(1.4)",
          }}
          decoding="async"
          draggable={false}
        />
      ) : null}

      {/* EYE GLOW — positioned wolf-eye lights that hold a higher floor
          than the ambient pulse. They watch you back. */}
      {eyePoints.map((p, i) => {
        const size = p.size ?? 64;
        const color = p.color ?? "rgba(195, 35, 35, 1)"; // ember-bright
        return (
          <div
            key={`eye-${i}`}
            className="absolute motion-safe:animate-eye-glow"
            style={{
              left: p.x,
              top: p.y,
              width: `${size}px`,
              height: `${size}px`,
              transform: "translate(-50%, -50%)",
              background: `radial-gradient(circle, ${color} 0%, ${replaceAlpha(
                color,
                0.55,
              )} 25%, ${replaceAlpha(color, 0)} 70%)`,
              mixBlendMode: "screen",
              animationDelay: p.delayMs ? `${p.delayMs}ms` : undefined,
              filter: "blur(2px)",
            }}
          />
        );
      })}

      {/* VIGNETTE — pulls focus to center. */}
      <div className="absolute inset-0" style={{ background: VIGNETTE }} />

      {/* EMBER BLOOM at base — atmospheric warmth from the war room floor. */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{
          background:
            "radial-gradient(ellipse at center bottom, rgba(154,29,29,0.20) 0%, rgba(5,6,8,0) 65%)",
        }}
      />

      {/* READABILITY SCRIM — flat darkening over the whole composed
          background so foreground panels stay legible. Tunable via `scrim`. */}
      {scrimStyle ? <div className="absolute inset-0" style={scrimStyle} /> : null}
    </div>
  );
}

/** Replace the alpha channel of an rgba() color string. */
function replaceAlpha(color: string, alpha: number): string {
  // Handle rgba(r,g,b,a) — what our config emits.
  const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return color;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}

/** Live-tuning URL params for eye glow positions. Dev affordance. */
function readDevEyeOverrides(
  search: string,
  fallback: GlowPoint[],
): GlowPoint[] | null {
  const params = new URLSearchParams(search);

  const hide = params.get("eyeHide");
  if (hide === "1" || hide === "true") return [];

  const y = params.get("eyeY");
  const x1 = params.get("eyeX1");
  const x2 = params.get("eyeX2");
  const size = params.get("eyeSize");

  // No params present — fall through to config defaults.
  if (!y && !x1 && !x2 && !size) return null;

  const left = fallback[0];
  const right = fallback[1];
  if (!left || !right) return null;

  const sizeNum = size ? Number.parseInt(size, 10) : undefined;

  return [
    {
      x: x1 ? `${x1}%` : left.x,
      y: y ? `${y}%` : left.y,
      size: sizeNum ?? left.size,
      color: left.color,
      delayMs: left.delayMs,
    },
    {
      x: x2 ? `${x2}%` : right.x,
      y: y ? `${y}%` : right.y,
      size: sizeNum ?? right.size,
      color: right.color,
      delayMs: right.delayMs,
    },
  ];
}

export default BackgroundLayer;
export type { Variant as BackgroundVariant, Overlay as BackgroundOverlay, GlowPoint };
